<template>
  <base-layout v-bind:show-content-header="false">
    <h1 slot="header">
      {{ `app.calendar.calendar` | trans }}
    </h1>
    <v-app id="vuetify-app">
      <v-container fluid grid-list-md>
        <v-layout wrap align-start justify-left>
          <v-flex shrink>
            <add-event-modal ref="addEventModal"/>
            <v-btn color="primary ml-0 mr-0" @click="changeStartDayToToday">
              {{ 'app.calendar.today' | trans }}
            </v-btn>
            <icon-button
              @click="$refs.calendar.prev()"
              icon="keyboard_arrow_left"
              color="primary"
            >
              {{trans('app.previous') }}
            </icon-button>
            <icon-button
              @click="$refs.calendar.next()"
              icon="keyboard_arrow_right"
              color="primary"
            >
              {{trans('app.next') }}
            </icon-button>
            <calendar-settings-modal/>
          </v-flex>
          <v-flex grow text-center>
            <h2>{{ currentDate }}</h2>
          </v-flex>
          <v-flex sm3>
              <search-calendar-event-input @changeDate="handleChangeDate"/>
          </v-flex>
          <v-flex sm1>
            <v-select
                v-model="view"
                :items="availableViews"
                :label="'app.calendar.view' | trans"
                :disabled="!hasPermission('calendar')"
                item-text="label"
                item-value="value"
                return-object
                @change="changeView"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout align-space-between justify-center row fill-height>
          <v-flex
              sm12
          >
            <v-alert
              :value="hasPermission('calendar') && (userRole === 'dietitian_standard' || userRole === 'dietitian_student')"
              type="info"
            >
              Wypróbuj przez miesiąc najnowszą funkcję z planu Premium. Możesz także rozszerzyć swój plan <a href="subscription/select">tutaj</a> lub poznać zalety planu Premium <a href="https://subscribepage.io/vFqBmm" target="_blank">tutaj</a>.
            </v-alert>
            <v-sheet class="calendar-sheet">
              <v-calendar
                  ref="calendar"
                  locale="pl-pl"
                  v-model="start"
                  :type="calendarSetting.view"
                  :start="start"
                  :end="end"
                  :min-weeks="1"
                  :max-days="maxDays"
                  :now="now"
                  :weekdays="weekdays"
                  :first-interval="intervals.first"
                  :interval-minutes="intervals.minutes"
                  :interval-count="intervals.count"
                  :interval-height="intervals.height"
                  :interval-format="intervalFormat()"
                  :show-interval-label="showIntervalLabel"
                  :color="color"
                  @click:day="handleMonthViewCalendarClick($event)"
                  @click:time="handleWeekAndDayViewCalendarClick($event)"
                  @click:date="handleDayClick($event)"
              >
                <template v-slot:day="day">
                  <v-sheet
                      v-if="calendarEventsMappedToDays.hasOwnProperty(day.date)"
                      v-for="event in calendarEventsMappedToDays[day.date]"
                      :key="event.id"
                      :color="getColor(event)"
                      class="white--text event-item mb-1 pointer"
                      @click.stop="editEvent(event)"
                  >
                    <v-layout>
                        <v-hover open-delay="500">
                          <v-flex
                              slot-scope="{ hover }"
                              :class="`${hover ? '' : 'event-name'}`"
                          >
                            {{ event.name }}
                          </v-flex>
                        </v-hover>
                      <v-flex class="text-right">
                        {{ getTimeFromDatetime(event.start_date) }}
                      </v-flex>
                    </v-layout>
                  </v-sheet>
                </template>
                <template v-slot:interval="{ date, timeToY, hour, time, minutesToPixels}">
                    <div
                        v-if="shouldRenderLine(hour, timeToY)"
                        class="v-current-time"
                        :class="{
                          first: shouldHaveFirstClass(date),
                        }"
                        :style="{ top: currentForCalculations }"
                    ></div>
                    <div
                        v-if="calendarEventsMappedToDays.hasOwnProperty(date) && hour === getEventHour(event)"
                        v-for="event in calendarEventsMappedToDays[date]"
                    >
                        <v-hover open-delay="500">
                          <div
                              :key="`day-view-${event.id}`"
                              :style="{
                                top: timeToY(getTimeFromCalendarEvent(event)) + 'px',
                                left: getIndent(event),
                                height: getSheetHeight(minutesToPixels, event),
                                background: getColor(event),
                              }"
                              :class="isShortEvent(minutesToPixels, event) ? 'my-event with-time short' : 'my-event with-time'"
                              @click.stop="editEvent(event)"
                              slot-scope="{ hover }"
                              v-html="prepareEventHtml(event, !hover)"
                              ref="weeklyAndDailyEvents"
                          ></div>
                        </v-hover>
                    </div>
                  </template>
              </v-calendar>
            </v-sheet>
          </v-flex>
        </v-layout>
      </v-container>
    </v-app>
  </base-layout>
</template>

<script>

import {mapActions, mapGetters} from "vuex";
import moment from "moment";
import {trans} from "../../app";
import axios from "axios";
import goTo from "vuetify/lib/components/Vuetify/goTo";
import {SYSTEM_DATE} from "../../utils/dateFormatter";

const weekdaysDefault = [1, 2, 3, 4, 5, 6, 0]

const intervalsDefault = {
  first: 0,
  minutes: 60,
  count: 24,
  height: 50
}

export default {
  data: () => ({
    current: {
      hour: moment().hour(),
      minute: moment().minute(),
    },
    timeToYFunction: undefined,
    highestEvent: null,
    start: moment().format(SYSTEM_DATE),
    end: moment().endOf('month').format(SYSTEM_DATE),
    now: null,
    type: 'month',
    ready: false,
    weekdays: weekdaysDefault,
    intervals: intervalsDefault,
    maxDays: 7,
    color: 'primary',
    view: null,
    availableViews: [
      {
        label: trans('app.calendar.views.day'),
        value: 'day',
      },
      {
        label: trans('app.calendar.views.week'),
        value: 'week',
      },
      {
        label: trans('app.calendar.views.month'),
        value: 'month',
      },
    ],
  }),
  created() {
    this.fetchCalendarSetting()
        .then(() => {
          this.view = this.calendarSetting.view;
        });
    this.fetchCalendarEvents()
        .then(() => {
          this.scrollToTime();

          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has('calendar_event')) {
            const calendarEventId = +urlParams.get('calendar_event');
            const calendarEvent = this.calendarEvents.find(ce => ce.id === calendarEventId);

            if (calendarEvent !== undefined) {
              this.editEvent(calendarEvent);
            }
          }
        });
  },
  beforeCreate() {
    const constSetCurrent = () => {
      const current = moment();
      const hour = current.hour();
      const minute = current.minute();
      this.current = {
        hour,
        minute,
      };
    };
    setTimeout(constSetCurrent, 60000);
  },
  mounted() {
    this.ready = true;
  },
  computed: {
    ...mapGetters(['calendarEvents', 'calendarSetting', 'USER', 'hasPermission', 'userRole']),
    calendarEventsMappedToDays() {
      const mapped = {};
      this.calendarEvents.forEach(calendarEvent => {
        calendarEvent.indent = 0;
        const key = calendarEvent.start_date.slice(0,10);
        if (mapped.hasOwnProperty(key)) {
          const length = mapped[key].push(calendarEvent);
          const e1 =mapped[key][length - 2];
          const e2 =mapped[key][length - 1];
          if (this.checkIfEventsOverlap(e1, e2)) {
              e2['indent'] = e1['indent'] + 25;
          }
        } else {
          mapped[key] = [calendarEvent];
        }
      });

      return mapped;
    },
    currentDate() {
        const currentDate = moment(this.start);

        return `${currentDate.format('MMMM')} ${currentDate.format('YYYY')}`;
    },
    cal () {
      return this.ready ? this.$refs.calendar : null;
    },
    currentForCalculations() {
      if (!this.$refs.calendar) {
        return '0px';
      }
      if (this.timeToYFunction !== undefined) {
        return this.timeToYFunction({hour: this.current.hour, minute: this.current.minute}) + 'px';
      }

      return this.$refs.calendar.timeToY({hour: this.current.hour, minute: this.current.minute}) + 'px';
    },
  },
  methods: {
    ...mapActions(['fetchCalendarEvents', 'fetchCalendarSetting']),
    shouldRenderLine(hour, timeToY) {
      if (this.timeToYFunction === undefined) {
        this.timeToYFunction = timeToY;
      }
      const currentDateTime = moment();
      const currentHour = currentDateTime.hour();

      return currentHour === hour;
    },
    checkIfEventsOverlap(event1, event2) {
        const startDateEvent1 = moment(event1.start_date);
        const endDateEvent1 = moment(event1.end_date);
        const startDateEvent2 = moment(event2.start_date);
        const endDateEvent2 = moment(event2.end_date);

        return startDateEvent1.isBetween(startDateEvent2, endDateEvent2)
            || endDateEvent1.isBetween(startDateEvent2, endDateEvent2)
            || startDateEvent2.isBetween(startDateEvent1, endDateEvent1)
            || endDateEvent2.isBetween(startDateEvent1, endDateEvent1)
            || startDateEvent1.isSame(startDateEvent2)
    },
    showIntervalLabel (interval) {
      return interval.minute === 0
    },
    getTimeFromCalendarEvent(event) {
      const startDate = moment(event.start_date);

      return {
        hour: +startDate.format('H'),
        minute: +startDate.format('m'),
      };
    },
    getDurationFromEvent(event) {
      return parseInt(moment(event.end_date).diff(moment(event.start_date)) / (1000 * 60));
    },
    getEventHour(event) {
      return moment(event.start_date).hour();
    },
    getColor(calendarEvent) {
      const colors = {
        yellow: '#f6bf26',
        orange: '#f4511e',
        red: '#d50000',
        violet: '#7986cb',
        blue: '#039be5',
        green: '#4caf50',
      };

      if (calendarEvent.label === null) {
        return '#616161';
      }

      if (colors.hasOwnProperty(calendarEvent.label.color)) {
        return colors[calendarEvent.label.color];
      }

      return '#616161';
    },
    editEvent(event) {
      if (this.hasPermission('calendar')) {
        this.$refs.addEventModal.openAndEdit(event);
      }
    },
    handleMonthViewCalendarClick({date}) {
      if (this.hasPermission('calendar')) {
        this.$refs.addEventModal.openAndEdit({
          id: null,
          label: null,
          name: '',
          start_date: `${date} 12:00:00`,
          end_date: `${date} 13:00:00`,
        });
      }
    },
    handleWeekAndDayViewCalendarClick({date, time, minute}) {
      if (this.hasPermission('calendar') === false) {
        return;
      }
      const hourTime = time.split(':')[0];
      let minuteTime = null;
      do {
          if (minute === 0) {
              minuteTime = '00';
          } else if (minute >= 45) {
              minuteTime = '45';
          } else if (minute % 15 === 0) {
              minuteTime = String(minute);
          }
          minute++;
      } while (minuteTime === null)
      const startDate = `${date} ${hourTime}:${minuteTime}:00`;
      const endTime = moment(startDate).add(1, 'hour').format('HH:mm');

      this.$refs.addEventModal.openAndEdit({
        id: null,
        label: null,
        name: '',
        start_date: startDate,
        end_date: `${date} ${endTime}:00`,
      });
    },
    async changeView() {
      await axios.patch(`/v2/calendar-settings/${this.USER.id}`, {
        view: this.view.value,
      });
      await this.fetchCalendarSetting();
      if (this.view.value !== 'month') {
          this.scrollToTime();
      }
    },
    getTimeFromDatetime(datetime) {
      return moment(datetime).format('HH:mm');
    },
    prepareEventHtml(event, withClass) {
      return `
      <div class="layout">
        <div class="flex ${withClass ? 'event-name' : ''}">
          ${event.name}
        </div>
        <div class="flex text-right">
          ${this.getTimeFromDatetime(event.start_date)}
        </div>
      </div>`;
    },
    intervalFormat() {
      return (datetime) => {
        return datetime.time;
      };
    },
    handleDayClick({date}) {
      if (this.hasPermission('calendar') === false) {
        return;
      }
      const startDate = `${date} 12:00:00`;
      const endDate = `${date} 13:00:00`;

      this.$refs.addEventModal.openAndEdit({
        id: null,
        label: null,
        name: '',
        start_date: startDate,
        end_date: endDate,
      });
    },
    changeStartDayToToday() {
        this.start = moment().format(SYSTEM_DATE);
    },
    handleChangeDate({date}) {
        this.start = date;
    },
    getSheetHeight(minutesToPixels, event) {
        const height = minutesToPixels(this.getDurationFromEvent(event));
        return `${height}px`;
    },
    isShortEvent(minutesToPixels, event) {
      return minutesToPixels(this.getDurationFromEvent(event)) < 25 ? true : false;
    },
    getIndent(event) {
        return `${event.indent}px`;
    },
    getCurrentTime () {
      return this.cal ? this.cal.times.now.hour * 60 + this.cal.times.now.minute : 0
    },
    scrollToTime () {
      const time = this.getCurrentTime()
      const first = Math.max(0, time - (time % 30) - 30)
      this.cal.scrollToTime(first)
    },
    shouldHaveFirstClass(date) {
      const view = this.view === null ? 'day' : this.view.value;

      return moment(date).weekday() === 0 || view === 'day';
    },
  },
}
</script>

<style lang="scss" scoped>
.feature-pane {
  position: relative;
  padding-top: 30px;
  box-shadow: 0 0 10px rgba(0,0,0,0.3);
}

.day-header {
  margin: 0px 2px 2px 2px;
  padding: 2px 6px;
  background-color: #1867c0;
  color: #ffffff;
  border: 1px solid #1867c0;
  border-radius: 2px;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.day-body {
  position: absolute;
  top: 400px;
  height: 36px;
  margin: 2px;
  padding: 2px 6px;
  background-color: #1867c0;
  color: #ffffff;
  border: 1px solid #1867c0;
  border-radius: 2px;
  left: 0;
  right: 0;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.day {
  position: relative;
  height: 24px;
  margin: 0px;
  padding: 0px 6px;
  background-color: #1867c0;
  color: #ffffff;
  border: 1px solid #1867c0;
  border-radius: 2px;
  left: 0;
  right: 0;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.my-event {
  border-radius: 2px;
  background-color: #1867c0;
  color: #ffffff;
  font-size: 12px;
  padding: 3px;
  cursor: pointer;
  margin-bottom: 1px;
  left: 4px;
  margin-right: 8px;
  position: relative;
}

.with-time {
  position: absolute;
  right: 4px;
  margin-right: 0px;
}

.short{
  font-size:11px;
  padding:0 3px;
}

.pointer, .v-calendar-weekly__day, .v-calendar-daily__day-interval {
  cursor: pointer;
}

::v-deep .v-calendar-daily__scroll-area{
    height: calc(100vh - 400px) !important;
}

::v-deep .application--wrap {
    min-height: calc(100vh - 80px) !important;
}

.event-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.event-item{
  font-size: 12px;
  padding: 2px;
}

.calendar-sheet {
  height: calc(100% - 80px) !important;
  width: 100%
}

::v-deep .v-calendar-daily__interval-text{
  top:0;
}

.v-current-time {
  height: 2px;
  background-color: #ea4335;
  position: absolute;
  left: -1px;
  right: 0;
  pointer-events: none;

  &.first::before {
    content: '';
    position: absolute;
    background-color: #ea4335;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: -5px;
    margin-left: -6.5px;
  }
}
</style>
